import React from "react";
import { useTranslation } from "react-i18next";
import { Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  useCosmopointsConnections,
  useDeleteCosmopointsConnection,
} from "@/api/hooks/use-query-api";
import { useToast } from "@/features/toast/hooks/use-toast";
import type { CosmopointsConnection } from "@/api/models/api-types";
import { ProgramLogo } from "../components/ProgramLogo";

interface LoyaltyProgramsSectionProps {
  /** Reward credits available to spend — nothing to convert at zero. */
  credits: number;
  onLink: () => void;
  onConvert: (connection: CosmopointsConnection) => void;
}

/**
 * Linked partner loyalty programs.
 *
 * Hidden entirely when the feature is off on the API — the connections query
 * fails and there is nothing useful to show, so we don't advertise a
 * redemption route the user cannot take.
 */
export const LoyaltyProgramsSection: React.FC<LoyaltyProgramsSectionProps> = ({
  credits,
  onLink,
  onConvert,
}) => {
  const { t } = useTranslation();
  const { success, error: showError } = useToast();

  const { data, isLoading, isError } = useCosmopointsConnections();
  const { mutateAsync: deleteConnection, isPending: isDeleting } =
    useDeleteCosmopointsConnection();

  if (isError) return null;

  const connections = data?.data ?? [];

  const handleUnlink = async (id: string, programName?: string) => {
    try {
      await deleteConnection(id);
      success(
        t("settings.rewards.cosmopoints.unlinkSuccess", {
          program: programName ?? t("settings.rewards.cosmopoints.name"),
        })
      );
    } catch {
      showError(t("settings.rewards.cosmopoints.unlinkError"));
    }
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-lg font-semibold text-text-primary dark:text-gray-100">
          {t("settings.rewards.cosmopoints.sectionTitle")}
        </h2>
        <Button
          type="button"
          variant="outline"
          onClick={onLink}
          className="px-3.5 py-2 rounded-[10px]"
        >
          {t("settings.rewards.cosmopoints.linkCta")}
        </Button>
      </div>

      <p className="text-sm text-text-quaternary dark:text-gray-400">
        {t("settings.rewards.cosmopoints.sectionDescription")}
      </p>

      {isLoading ? (
        <div className="h-20 bg-gray-50 dark:bg-gray-700 rounded-xl animate-pulse" />
      ) : connections.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border-secondary dark:border-gray-600 p-6 text-center">
          <p className="text-sm text-text-quaternary dark:text-gray-400">
            {t("settings.rewards.cosmopoints.empty")}
          </p>
        </div>
      ) : (
        <ul className="flex flex-col gap-3">
          {connections.map((connection) => (
            <li
              key={connection._id}
              className="flex flex-col gap-3 rounded-xl border border-border-secondary dark:border-gray-600 bg-bg-primary dark:bg-gray-800 p-4 sm:flex-row sm:items-center sm:justify-between sm:gap-4"
            >
              <div className="flex items-center gap-3 min-w-0">
                <ProgramLogo src={connection.programLogo} />
                <div className="flex flex-col gap-0.5 min-w-0">
                  <p className="truncate text-sm font-semibold text-text-primary dark:text-gray-100">
                    {connection.programName ?? connection.programId}
                  </p>
                  <p className="truncate text-sm text-text-quaternary dark:text-gray-400">
                    {connection.destinationAccount}
                  </p>
                </div>
              </div>

              {/* What this program has actually paid the user so far. Shown
                  even at zero — a freshly linked program should say so rather
                  than leave the reader guessing whether it tracks anything. */}
              <div className="flex items-baseline gap-1.5 sm:ml-auto sm:shrink-0 sm:flex-col sm:items-end sm:gap-0.5">
                <span className="text-base font-semibold text-text-primary dark:text-gray-100">
                  {(connection.pointsEarned ?? 0).toLocaleString()}
                </span>
                <span className="text-xs text-text-quaternary dark:text-gray-400">
                  {t("settings.rewards.cosmopoints.pointsEarnedLabel")}
                </span>
              </div>

              <div className="flex items-center gap-1 sm:shrink-0">
                {/* Full width on mobile where the row stacks, hugging its
                    label once the row goes horizontal. */}
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => onConvert(connection)}
                  disabled={credits <= 0}
                  className="h-auto min-h-11 flex-1 whitespace-normal px-3.5 py-2 rounded-[10px] text-sm disabled:opacity-50 disabled:cursor-not-allowed sm:flex-none sm:whitespace-nowrap"
                >
                  {t("settings.rewards.cosmopoints.convertCta")}
                </Button>

                <button
                  type="button"
                  onClick={() =>
                    handleUnlink(connection._id, connection.programName)
                  }
                  disabled={isDeleting}
                  aria-label={t("settings.rewards.cosmopoints.unlink")}
                  title={t("settings.rewards.cosmopoints.unlink")}
                  className="shrink-0 rounded-lg p-2 text-zinc-500 transition-colors hover:bg-gray-100 hover:text-destructive disabled:opacity-50 dark:text-gray-400 dark:hover:bg-gray-700"
                >
                  <Trash2 className="h-5 w-5" />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
